import { useStitchAuth } from '../../StitchAuth';
import { useState, useEffect } from 'react';
import { getGroupById } from "../../../services/GroupsApi";
import { getDeviceCounts } from "../../../services/RegistryApi";

/**
 * Hook to check monthly device ordering limits
 * @param {boolean} [detailedInfo=false] - If true, returns detailed limit information
 * @returns {boolean|Object} - Boolean if limit is triggered, or object with detailed info
 */
export function useDeviceLimitTrigger(detailedInfo = false) {
  const { userCustomData } = useStitchAuth();
  const [deviceLimitInfo, setDeviceLimitInfo] = useState({
    isLimitReached: false,
    totalOrdered: 0,
    monthlyLimit: 2, // Default fallback
    remaining: 2
  });

  useEffect(() => {
    if (!userCustomData?.group_id) return;

    let isMounted = true;

    async function checkDeviceLimit() {
      try {
        // Fetch group limit
        let monthlyLimit = 25; // Default fallback
        try {
          const group = await getGroupById(userCustomData.group_id);
          if (isMounted && group) {
            // Check for new field name first, then fall back to old field name
            monthlyLimit = group.monthlyDeviceOrderLimit || group.monthly_device_orders || monthlyLimit;
          }
        } catch (error) {
          console.error("Error fetching group device limit:", error);
          // Continue with default value
        }

        // Use the new device counts API to get current counts for this group
        const counts = await getDeviceCounts(userCustomData.group_id);
        if (!isMounted) return;

        const totalDevicesOrdered = counts.data?.totalDevices || 0;
        const isLimitReached = totalDevicesOrdered >= monthlyLimit;
        const remaining = Math.max(0, monthlyLimit - totalDevicesOrdered);

        setDeviceLimitInfo({
          isLimitReached,
          totalOrdered: totalDevicesOrdered,
          monthlyLimit: monthlyLimit,
          remaining: remaining
        });
      } catch (error) {
        if (isMounted) {
          console.error("Error checking device limits:", error);
        }
      }
    }

    checkDeviceLimit();

    return () => {
      isMounted = false;
    };
  }, [userCustomData]);

  // Return detailed info or just the boolean based on the parameter
  return detailedInfo ? deviceLimitInfo : deviceLimitInfo.isLimitReached;
}